import type { CalendarEvent, TaskBlock } from "../../api/generated";
import { formatTime } from "../../utils/calendarDay";
import { MIN_MINUTES, minutesInto, placeInColumns } from "../../utils/dayLayout";

interface PersonaLaneProps {
  /** The persona's timed events, already without hidden calendars. */
  events: CalendarEvent[];
  /** Time planned for the persona's tasks. */
  blocks: TaskBlock[];
  range: { from: string; to: string };
  /** The hour at the top of the grid, e.g. 7 for 07:00. */
  firstHour: number;
  hourHeight: number;
  colorOf: (calendarId: string) => string;
  /** The persona's colour, for its planned blocks. */
  blockColor: string;
  now: Date;
}

type Item = { key: string; startsAt: string; endsAt: string } & ({ kind: "event"; event: CalendarEvent } | { kind: "block"; block: TaskBlock });

/**
 * One persona's column of timed events and planned task blocks, placed by time, with overlapping
 * ones side by side. Planned blocks have a solid bar on the left and faint stripes, so they don't
 * read as meetings.
 */
export function PersonaLane({ events, blocks, range, firstHour, hourHeight, colorOf, blockColor, now }: PersonaLaneProps) {
  const items: Item[] = [
    ...events.map((event) => ({ key: event.id, startsAt: event.startsAt, endsAt: event.endsAt, kind: "event" as const, event })),
    ...blocks.map((block) => ({ key: block.id, startsAt: block.startsAt, endsAt: block.endsAt, kind: "block" as const, block })),
  ];
  const spans = items.map((item) => ({ item, start: minutesInto(range, item.startsAt), end: minutesInto(range, item.endsAt) }));
  const y = (minutes: number) => ((minutes - firstHour * 60) / 60) * hourHeight;

  return (
    <div className="relative border-l border-border-subtle">
      {placeInColumns(spans).map(({ item, start, end, col, cols }) => {
        const height = Math.max(y(Math.max(end, start + MIN_MINUTES)) - y(start) - 2, 14);
        const showTime = height >= 32;
        const isBlock = item.kind === "block";
        const color = isBlock ? blockColor : colorOf(item.event.calendarId);
        const title = isBlock ? item.block.title : item.event.summary || "(No title)";
        const times = `${formatTime(item.startsAt)}–${formatTime(item.endsAt)}`;
        const link = isBlock ? null : item.event.htmlLink;
        const faded = (isBlock && item.block.done) || new Date(item.endsAt) <= now;
        const Block = link ? "a" : "div";
        const edge = `color-mix(in oklab, ${color} 45%, transparent)`;
        return (
          <Block
            key={item.key}
            href={link ?? undefined}
            target={link ? "_blank" : undefined}
            rel={link ? "noreferrer" : undefined}
            title={`${isBlock ? "Planned: " : ""}${title} · ${times}`}
            className="absolute flex flex-col gap-0.5 overflow-hidden rounded-md text-text-primary no-underline"
            style={{
              top: y(start) + 1,
              height,
              left: `calc(${(col / cols) * 100}% + 3px)`,
              width: `calc(${100 / cols}% - 6px)`,
              padding: showTime ? "5px 8px" : "0 8px",
              justifyContent: showTime ? "flex-start" : "center",
              opacity: faded ? 0.6 : 1,
              ...(isBlock
                ? {
                    background: `repeating-linear-gradient(135deg, color-mix(in oklab, ${color} 10%, var(--surface-card)) 0 6px, var(--surface-card) 6px 12px)`,
                    border: `1px solid ${edge}`,
                    borderLeft: `3px solid ${color}`,
                  }
                : {
                    background: `color-mix(in oklab, ${color} 16%, var(--surface-card))`,
                    border: `1px ${item.event.status === "tentative" ? "dashed" : "solid"} ${edge}`,
                  }),
            }}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              {!isBlock && <span className="size-1.5 shrink-0 rounded-full" style={{ background: color }} />}
              <span className={`truncate text-xs leading-[14px] font-medium ${isBlock && item.block.done ? "line-through" : ""}`}>{title}</span>
            </span>
            {showTime && <span className="truncate font-mono text-[10.5px] text-text-secondary">{isBlock ? `Planned · ${times}` : times}</span>}
          </Block>
        );
      })}
    </div>
  );
}
